import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft, CheckCircle2, Clock, Headphones, ImagePlus, Mail, RefreshCw, Send, ShieldCheck, Zap } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import SupportAgentPicker, { getAgentProfile } from "../components/SupportAgentPicker";
import { siteConfig, storageKey } from "../lib/siteConfig";
import ImagePreviewModal from "../components/ImagePreviewModal";
import LinkifiedText from "../components/LinkifiedText";
import { VerifiedName } from "../components/VerifiedBadge";
import { useSupportAttachment } from "../lib/supportMedia";
import { SupportAttachmentPreview, SupportVideo, supportMessageImages } from "../components/SupportMedia";

const guestKey = storageKey("guest_chat_id");
const supportEmail = siteConfig.supportEmail;
const initialMessageCount = 5;
const messageLoadStep = 8;
const SUPPORT_POLL_MS = 12000;

function getGuestId() {
  let id = localStorage.getItem(guestKey);
  if (!id) {
    id = crypto?.randomUUID?.() || `guest-${Date.now()}-${Math.random().toString(16).slice(2)}`;
    localStorage.setItem(guestKey, id);
  }
  return id;
}

export default function Support() {
  const nav = useNavigate();
  const location = useLocation();
  const { user, loading: authLoading } = useAuth();
  const isAuthed = user && user !== false;
  const [guestId] = useState(getGuestId);
  const [chatTicket, setChatTicket] = useState(null);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const { attachment, setAttachment, select: selectAttachment, upload: uploadAttachment, progress: uploadProgress, cancel: cancelUpload } = useSupportAttachment(guestId);
  const [visibleMessageCount, setVisibleMessageCount] = useState(initialMessageCount);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const scrollerRef = useRef(null);
  const fileRef = useRef(null);
  const messageRef = useRef(null);
  const selectedProfile = getAgentProfile(selectedAgent);
  const aiReplyPending = ["pending", "processing"].includes(chatTicket?.ai_reply_status ?? chatTicket?.ai_first_reply_status);

  const loadChat = useCallback(async () => {
    if (authLoading) return;
    try {
      if (isAuthed) {
        const { data } = await api.get("/support/my");
        setChatTicket(data.find((t) => t.subject === "Live agent chat") || null);
        return;
      }
      const { data } = await api.get(`/support/chat/${guestId}`);
      setChatTicket(data?.id ? data : null);
    } catch {
      setChatTicket((current) => current);
    }
  }, [authLoading, guestId, isAuthed]);

  useEffect(() => { loadChat(); }, [loadChat]);

  useEffect(() => {
    if (authLoading) return undefined;
    const timer = setInterval(() => {
      if (document.visibilityState !== "hidden") loadChat();
    }, aiReplyPending ? 2000 : SUPPORT_POLL_MS);
    return () => clearInterval(timer);
  }, [authLoading, loadChat, aiReplyPending]);

  const goBack = useCallback(() => {
    const from = location.state?.from;
    if (typeof from === "string" && from && from !== location.pathname) {
      nav(from, { replace: true });
      return;
    }
    if (location.key && location.key !== "default") {
      nav(-1);
      return;
    }
    nav(isAuthed ? "/dashboard" : "/", { replace: true });
  }, [isAuthed, location.key, location.pathname, location.state, nav]);

  const selectImage = async (file) => {
    if (!file || loading) return;
    try {
      setErr("");
      selectAttachment(file);
    } catch (e) {
      setErr(e.message);
    } finally {
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    const text = message.trim();
    const picked = attachment;
    if (loading || (!text && !picked)) return;
    if (!selectedProfile) {
      setErr("Choose Fea, Mellisa, or Jennifer first.");
      return;
    }
    setErr("");
    setLoading(true);
    try {
      const attachmentId = await uploadAttachment(picked);
      const { data } = await api.post("/support/chat", {
        guest_id: isAuthed ? undefined : guestId,
        name: isAuthed ? user.username : "Guest",
        email: isAuthed ? user.email : undefined,
        message: text,
        agent_target: selectedProfile?.name,
        attachment_id: attachmentId,
      });
      if (picked) picked.sent = true;
      setMessage("");
      setAttachment(null);
      setChatTicket(data);
      setTimeout(loadChat, 150);
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  const messages = useMemo(() => scopedAgentMessages(chatTicket, selectedProfile), [chatTicket, selectedProfile]);
  const hiddenMessageCount = Math.max(0, messages.length - visibleMessageCount);
  const visibleMessages = useMemo(
    () => messages.slice(Math.max(0, messages.length - visibleMessageCount)),
    [messages, visibleMessageCount],
  );

  const pickAgent = (agent) => {
    setSelectedAgent(agent);
    setErr("");
    window.setTimeout(() => messageRef.current?.focus(), 0);
  };

  useEffect(() => {
    setVisibleMessageCount(initialMessageCount);
  }, [selectedProfile?.name]);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [chatTicket?.last_message_at, messages.length]);

  return (
    <div className="support-page min-h-[100dvh] bg-slate-50 text-slate-950">
      <header className="sticky top-0 z-30 bg-white/90 backdrop-blur-xl border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <button onClick={goBack} className="p-2 rounded-full hover:bg-slate-100" aria-label="Back">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <img src={siteConfig.logoUrl} alt="" className="w-10 h-10 rounded-full object-cover ring-1 ring-[#2F6FA5]/60" />
            <div className="min-w-0">
              <h1 className="font-display text-lg font-bold truncate">{siteConfig.name} Support</h1>
              <p className="text-xs text-emerald-300">Online queue</p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button onClick={loadChat} className="p-2 rounded-full hover:bg-slate-100" aria-label="Refresh">
              <RefreshCw className="w-5 h-5" />
            </button>
            <a href={`mailto:${supportEmail}`} className="p-2 rounded-full hover:bg-slate-100" aria-label="Email support">
              <Mail className="w-5 h-5" />
            </a>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto w-full px-0 md:px-8 py-0 md:py-5 grid lg:grid-cols-[320px_minmax(0,1fr)] gap-0 md:gap-5">
        <aside className="hidden lg:block space-y-3">
          <div className="rounded-2xl border border-[#2F6FA5]/20 bg-white p-4 shadow-sm">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-11 h-11 rounded-full bg-[#2F6FA5]/15 border border-[#2F6FA5]/40 grid place-items-center">
                <Headphones className="w-5 h-5 text-[#2F6FA5]" />
              </div>
              <div>
                <p className="font-semibold">Support desk</p>
                <p className="text-xs text-slate-500">Replies stay in this app</p>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2 text-center">
              <Info icon={Zap} label="Fast" />
              <Info icon={ShieldCheck} label="Secure" />
              <Info icon={Clock} label="24/7" />
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-3 space-y-2 shadow-sm">
            {["Load money", "Redeem help", "Game login", "Account issue"].map((text) => (
              <button
                key={text}
                onClick={() => setMessage(text)}
                className="w-full flex items-center justify-between rounded-xl px-3 py-3 bg-slate-50 hover:bg-[#EAF4FC] border border-slate-200 text-sm"
              >
                <span>{text}</span>
                <CheckCircle2 className="w-4 h-4 text-[#2F6FA5]" />
              </button>
            ))}
          </div>

          <Link to="/" className="hidden lg:block text-center text-sm text-slate-500 hover:text-slate-950 py-2">
            Back to {siteConfig.name}
          </Link>
        </aside>

        <section className="bg-white overflow-hidden min-h-[calc(100dvh-4rem)] md:min-h-[calc(100vh-150px)] flex flex-col md:rounded-2xl md:border md:border-[#2F6FA5]/20 md:shadow-sm">
          <div className="px-4 py-3 border-b border-slate-200 bg-white flex items-center justify-between">
            <div>
              <p className="font-semibold">Agent chat</p>
              <p className="text-xs text-slate-500">{chatTicket?.status ? chatTicket.status.replace("_", " ") : "Ready"}</p>
            </div>
            <span className="inline-flex items-center gap-1.5 text-xs text-emerald-300">
              <span className="w-2 h-2 rounded-full bg-emerald-400" /> Active
            </span>
          </div>

          <div className="border-b border-slate-200 bg-slate-50 p-3">
            <SupportAgentPicker selectedAgent={selectedAgent} onPick={pickAgent} />
          </div>

          <div ref={scrollerRef} className="flex-1 overflow-y-auto px-4 py-5 space-y-3 bg-slate-50">
            {hiddenMessageCount > 0 && (
              <div className="flex justify-center pb-1">
                <button
                  type="button"
                  onClick={() => setVisibleMessageCount((count) => Math.min(messages.length, count + messageLoadStep))}
                  className="rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-600 hover:border-[#2F6FA5]/50 hover:text-slate-950"
                >
                  Load earlier messages ({hiddenMessageCount})
                </button>
              </div>
            )}
            {visibleMessages.map((msg) => (
              <ChatBubble
                key={msg.id || `${msg.sender}-${msg.created_at}`}
                msg={msg}
                onImagePreview={setImagePreview}
                user={isAuthed ? user : null}
              />
            ))}
            {err && (
              <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3">
                {err}
              </div>
            )}
          </div>

          <form onSubmit={submit} className="p-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] md:p-4 bg-white border-t border-slate-200">
            <SupportAttachmentPreview attachment={attachment} progress={uploadProgress} busy={loading}
              onRemove={() => setAttachment(null)} onCancel={cancelUpload} />
            <div className="flex items-end gap-2">
              <input ref={fileRef} type="file" accept="image/*,video/*" disabled={loading} className="hidden" onChange={(e) => selectImage(e.target.files?.[0])} />
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                disabled={loading}
                title="Add photo or video"
                aria-label="Add photo or video"
                className="w-12 h-12 rounded-full bg-white border border-slate-200 text-slate-700 grid place-items-center hover:bg-slate-50 shrink-0"
              >
                <ImagePlus className="w-4 h-4" />
              </button>
              <textarea
                disabled={loading}
                ref={messageRef}
                rows={1}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    submit(e);
                  }
                }}
                placeholder={selectedProfile ? `Message ${selectedProfile.name}...` : "Choose an agent first..."}
                className="min-h-[48px] max-h-32 flex-1 resize-none rounded-2xl bg-white border border-slate-200 px-4 py-3 text-sm text-slate-950 focus:border-[#2F6FA5] focus:outline-none focus:ring-2 focus:ring-[#2F6FA5]/20"
              />
              <button
                type="submit"
                disabled={!selectedProfile || loading || (!message.trim() && !attachment)}
                className="w-12 h-12 rounded-full bg-[#2F6FA5] text-white grid place-items-center hover:bg-[#4E91C8] disabled:opacity-50 shrink-0"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
          </form>
        </section>
      </main>
      <ImagePreviewModal image={imagePreview} onClose={() => setImagePreview(null)} />
    </div>
  );
}

function Info({ icon: Icon, label }) {
  return (
    <div className="rounded-xl bg-slate-50 border border-slate-200 py-3">
      <Icon className="w-4 h-4 mx-auto text-[#2F6FA5] mb-1" />
      <div className="text-[11px] text-slate-500">{label}</div>
    </div>
  );
}

function normalizeMessages(ticket) {
  if (!ticket) return [];
  if (Array.isArray(ticket.messages) && ticket.messages.length) return ticket.messages;
  const out = [];
  if (ticket.message) out.push({ id: `${ticket.id}-user`, sender: "user", message: ticket.message, created_at: ticket.created_at });
  if (ticket.admin_reply) out.push({ id: `${ticket.id}-agent`, sender: "agent", message: ticket.admin_reply, created_at: ticket.updated_at || ticket.created_at });
  return out;
}

function scopedAgentMessages(ticket, selectedProfile) {
  const ticketMessages = normalizeMessages(ticket);
  if (!selectedProfile) {
    return [
      {
        id: "welcome-choose-agent",
        sender: "agent",
        message: "Choose Fea, Mellisa, or Jennifer to start a personal chat.",
        created_at: "",
      },
      ...ticketMessages.filter((msg) => !getAgentProfile(msg.agent_target)),
    ];
  }
  const agentName = selectedProfile.name;
  return [
    {
      id: `welcome-${agentName}`,
      sender: "agent",
      agent_target: agentName,
      message: `You're now messaging ${agentName}.`,
      created_at: "",
    },
    ...ticketMessages.filter((msg) => getAgentProfile(msg.agent_target)?.name === agentName),
  ];
}

function ChatBubble({ msg, onImagePreview, user }) {
  const isAgent = msg.sender === "agent";
  const target = getAgentProfile(msg.agent_target)?.name;
  return (
    <div className={`flex ${isAgent ? "justify-start" : "justify-end"}`}>
      <div className={`max-w-[88%] sm:max-w-[82%] rounded-2xl px-3 py-2 text-sm border shadow-sm ${
        isAgent
          ? "bg-white border-slate-200 text-slate-800 rounded-bl-md"
          : "bg-[#2F6FA5] border-[#2F6FA5] text-white rounded-br-md"
      }`}>
        <div className={`text-[10px] mb-1 ${isAgent ? "text-slate-500" : "text-white/75"}`}>
          {isAgent ? (msg.is_ai ? (msg.ai_generated ? "AI Support" : "Automated Support") : target || "Agent") : (
            <VerifiedName
              className="[&>svg]:h-3.5 [&>svg]:w-3.5"
              name={user?.username || "You"}
              user={user}
            />
          )}
        </div>
        {supportMessageImages(msg).map((image, index) => (
          <button
            key={`${image.image_url}-${index}`}
            type="button"
            onClick={() => onImagePreview?.({ src: image.image_url, name: image.image_name || "Attachment" })}
            className="mb-2 block max-w-full cursor-zoom-in rounded-xl bg-slate-100 text-left outline-none ring-[#2F6FA5]/60 focus:ring-2"
            aria-label="Open image preview"
          >
            <img src={image.image_url} alt={image.image_name || "Attachment"} className="block max-h-44 max-w-full rounded-xl object-contain sm:max-h-56" />
          </button>
        ))}
        <SupportVideo message={msg} />
        {msg.message && (
          <div className="whitespace-pre-wrap break-words leading-relaxed">
            <LinkifiedText
              text={msg.message}
              linkClassName={`font-semibold underline underline-offset-2 hover:opacity-80 ${isAgent ? "text-[#2F6FA5]" : "text-white"}`}
            />
          </div>
        )}
      </div>
    </div>
  );
}
